import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { useHomeworks } from '@/services/homeworks';
import { GlobalHeader } from '@/components/GlobalHeader';

interface ActivityStats {
  flashcardsReviewed: number;
  quizAverage: number;
  explainTopics: string[];
}

export function ProgressPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useAuthStore();
  const { data: homeworksData } = useHomeworks(childId ?? '');

  const { data: stats, isLoading } = useData<ActivityStats>(
    childId ? `/children/${childId}/activity-stats` : null,
  );

  // Homework completion per subject
  const homeworks = homeworksData?.homeworks ?? [];
  const subjectStats = homeworks.reduce<Record<string, { total: number; done: number }>>((acc, hw) => {
    const subj = hw.subject;
    if (!acc[subj]) acc[subj] = { total: 0, done: 0 };
    acc[subj].total += hw.exercises.length;
    acc[subj].done += hw.exercises.filter((e) => e.status === 'done').length;
    return acc;
  }, {});
  const homeworkSubjects = Object.entries(subjectStats);

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content">

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 24px' }}>
            {t('student_progress.title')}
          </h1>

          {/* ── Stats grid ── */}
          {isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div className="loading-skeleton" style={{ height: '96px', borderRadius: '12px' }} />
              <div className="loading-skeleton" style={{ height: '96px', borderRadius: '12px' }} />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                background: 'white', padding: '16px', borderRadius: '12px',
                border: '1px solid var(--border)', textAlign: 'center',
              }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 8px', fontWeight: 500 }}>
                  {t('student_progress.flashcards_flipped')}
                </p>
                <h3 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary)', margin: 0 }}>
                  {stats?.flashcardsReviewed ?? 0}
                </h3>
              </div>
              <div style={{
                background: 'white', padding: '16px', borderRadius: '12px',
                border: '1px solid var(--border)', textAlign: 'center',
              }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 8px', fontWeight: 500 }}>
                  {t('student_progress.quiz_average')}
                </p>
                <h3 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--success)', margin: 0 }}>
                  {stats?.quizAverage ?? 0}%
                </h3>
              </div>
            </div>
          )}

          {/* ── Homework Completed by Subject ── */}
          {homeworkSubjects.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('student_progress.homework_completed_heading')}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {homeworkSubjects.map(([subject, s]) => {
                  const pct = s.total > 0 ? Math.round((s.done / s.total) * 100) : 0;
                  return (
                    <div key={subject} style={{
                      background: 'white', borderRadius: '12px', padding: '14px 16px',
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)' }}>{subject}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {t('student_progress.exercise_count', { done: s.done, total: s.total })}
                        </span>
                      </div>
                      <div style={{ width: '100%', background: '#E5E7EB', borderRadius: '999px', height: '6px' }}>
                        <div style={{
                          width: `${pct}%`,
                          background: pct === 100 ? 'var(--success)' : 'var(--primary)',
                          height: '6px', borderRadius: '999px', transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Recently Explored Topics ── */}
          {(stats?.explainTopics ?? []).length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('student_progress.recent_topics')}
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(stats?.explainTopics ?? []).map((topic) => (
                  <span
                    key={topic}
                    style={{
                      background: 'var(--primary-light)',
                      borderRadius: '999px',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      color: 'var(--primary)',
                      fontWeight: 500,
                    }}
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Confidence CTA ── */}
          <div style={{
            background: 'var(--primary-light)',
            padding: '16px',
            borderRadius: '12px',
            marginTop: '32px',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
              {t('student_progress.algo_title')}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0 0 16px', lineHeight: 1.5 }}>
              {t('student_progress.algo_desc')}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/student/confidence')}
              style={{ width: '100%' }}
            >
              {t('student_progress.review_btn')}
            </button>
          </div>

        </div>

        <BottomTabBar activeTab="Progress" />
      </div>
    </div>
  );
}
